// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@ces/XDL/types";
import color from "@ces/XDL/Utilities/color";
import { styledScrollbar } from "@ces/XDL/Utilities/scroll";
import { TabProps } from "@insite/mobius/Tab";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const tabSelectedStyle = css`
    outline-width: 0;
    background: transparent;
    opacity: 1;
    color: ${color("text.visited")};
    &:hover {
        color: ${color("text.link")};
    }
    &:before {
        width: 32px;
        background: linear-gradient(to right, ${color("text.link")}, ${color("common.ui12")});
        clip-path: polygon(0% 0%, 90% 0%, 100% 50%, 90% 100%, 0% 100%);
    }
`;

const tabStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    padding-right: 32px;
    min-width: initial;
    user-select: none;
    position: relative;
    border: none;
    transition: all 0.5s;
    opacity: 0.5;
    color: ${color("text.link")};
    &:hover {
        opacity: 1;
        cursor: pointer;
        border: none;
    }
    &:focus {
        ${tabSelectedStyle}
    }
    &:before {
        transition: all 0.5s;
        content: "";
        width: 16px;
        height: 12px;
        clip-path: polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%);
        background: ${color("text.link")};
        display: block;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            variant: "h4",
            as: "p",
            css: css`
                margin-bottom: 0;
            `,
        },
        css: css`
            ${tabStyle}
            ${({ selected }: TabProps) => selected && tabSelectedStyle}
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            tabGroup: css`
                padding: 0;
                position: relative;
                align-items: flex-start;
                ${styledScrollbar()}
            `,
            tabContent: css`
                margin-top: 0;
                border: none;
                background: transparent;
                box-shadow: none;
                padding: 20px 0 0;
                @media print {
                    padding: 0;
                    margin-bottom: 12px;
                    display: block;

                    & > ${TypographyStyle} {
                        display: inline-block;
                        ${tabStyle}
                        ${tabSelectedStyle}
                    }

                    & > div {
                        padding: 0;
                        border: 1px solid ${color("common.border")};
                    }
                }
            `,
            wrapper: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["tab"];
